import { useCallback, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Box, Chip, FormControl, MenuItem, Select, SelectChangeEvent, Stack } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { Label, selectStyles } from './styles';
import { DifficultyLevel, useUpdateWorkflowStatusMutation, Zone } from '@/__generated__/graphql';
import * as state from '@/state';
import { DifficultyLevelColorsMap } from '@/theme/colors';
import { useSnackBarMessage } from '@/components/ZonesManagementRevamped/utils';

type DifficultySelectorProps = {
  difficulty: DifficultyLevel | string;
  zoneId: string;
};

export const DifficultySelector = ({ difficulty, zoneId }: DifficultySelectorProps) => {
  const structureId = useAtomValue(state.selectedStructureId);
  const setZones = useSetAtom(state.zones);
  const [open, setOpen] = useState(false);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const [updateDifficultyLevel] = useUpdateWorkflowStatusMutation();

  const handleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      if (!structureId || !zoneId) return;

      updateDifficultyLevel({
        variables: {
          input: {
            structureId,
            zoneIds: [zoneId],
            difficultyLevel: event.target.value as DifficultyLevel,
          },
        },
        onCompleted: (response) => {
          const updatedWorkflowStatus = response?.updateWorkflowStatus?.find(
            (zone) => zone?.id === zoneId
          );
          setZones((oldZones) =>
            oldZones.map((zone) =>
              zone.id === zoneId
                ? {
                    ...zone,
                    workflowStatus: updatedWorkflowStatus?.workflowStatus as Zone['workflowStatus'],
                  }
                : zone
            )
          );
          showSnackBar('The difficulty level has been updated successfully', 'success');
        },
        onError: (error) => {
          console.error(error);
          showSnackBar(error.message, 'error');
        },
      });
    },
    [structureId, zoneId, updateDifficultyLevel, setZones, showSnackBar]
  );

  const renderChip = (value: string) => {
    const ArrowIcon = open ? ArrowDropUpIcon : ArrowDropDownIcon;

    return (
      <Chip
        clickable
        size="small"
        label={
          <>
            <Box component="span" sx={{ flexShrink: 0 }}>
              {value}
            </Box>
            <ArrowIcon fontSize="small" />
          </>
        }
        sx={{
          position: 'absolute',
          right: 0,
          display: 'inline-flex',
          alignItems: 'center',
          width: '8rem',
          px: 1,
          py: 0.5,
          color: DifficultyLevelColorsMap[value]?.color,
          bgcolor: DifficultyLevelColorsMap[value]?.backgroundColor,
          fontWeight: 400,
          fontSize: '1rem',
          '& .MuiChip-label': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            padding: 0,
          },
        }}
      />
    );
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      sx={{
        pt: 2,
        width: '100%',
      }}
    >
      <Label>Difficulty</Label>
      <FormControl size="small" sx={{ flexGrow: 1, position: 'relative' }}>
        <Select
          size="small"
          value={difficulty}
          onChange={handleChange}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          variant="standard"
          disableUnderline
          IconComponent={() => null}
          renderValue={renderChip}
          sx={{ selectStyles }}
        >
          {Object.values(DifficultyLevel).map((option) => (
            <MenuItem key={option} value={option}>
              <Chip
                size="small"
                label={option}
                sx={{
                  color: DifficultyLevelColorsMap[option]?.color,
                  bgcolor: DifficultyLevelColorsMap[option]?.backgroundColor,
                  fontWeight: 400,
                  fontSize: '1rem',
                }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
};
